import { useEffect, useRef, useState } from 'react';
import { Check, CheckCheck, Hash, Lock, MoreVertical, Pencil, Send, Trash2, UserPlus, X } from 'lucide-react';
import type { ChatChannel, ChatMessage } from '@unitly/contracts';
import {
  useAddChannelMember, useChannelAction, useChatMessages, useEditMessage, useRenameConversation, useSendMessage, useWorkspace,
} from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { Sheet } from '../../ui/Sheet.tsx';
import { Avatar, EmptyState, PageLoader } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';

const EDIT_WINDOW_MS = 5 * 60_000;

function ChatMessageRow({ message, channel, orgId }: { message: ChatMessage; channel: ChatChannel; orgId: string }) {
  const edit = useEditMessage(orgId, channel.id);
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body);
  const editable = message.mine && Date.now() - new Date(message.createdAt).getTime() < EDIT_WINDOW_MS;

  if (editing) {
    return (
      <div className={`chat-msg${message.mine ? ' mine' : ''}`}>
        <div className="chat-bubble" style={{ maxWidth: '84%' }}>
          <input
            className="input" style={{ height: 32, fontSize: 14.5 }} autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
          />
          <div className="chat-time" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
            <button type="button" className="icon-btn muted" style={{ width: 22, height: 22 }} aria-label="Annulla" onClick={() => setEditing(false)}><X size={13} /></button>
            <button
              type="button" className="icon-btn muted" style={{ width: 22, height: 22 }} aria-label="Salva" disabled={!draft.trim() || edit.isPending}
              onClick={() => edit.mutate({ id: message.id, body: draft.trim() }, { onSuccess: () => setEditing(false), onError: (e) => toast(e instanceof ApiError ? e.message : 'Modifica non riuscita') })}
            >
              <Check size={13} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`chat-msg${message.mine ? ' mine' : ''}`}>
      {!message.mine && channel.kind !== 'dm' && <Avatar id={message.author.id} name={message.author.displayName} size={28} />}
      <div className="chat-bubble">
        {!message.mine && channel.kind !== 'dm' && <div className="chat-author">{message.author.displayName}</div>}
        <div>{message.body}</div>
        <div className="chat-time">
          {message.editedAt && <span>modificato ·</span>}
          {new Date(message.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
          {message.mine && channel.kind === 'dm' && (message.seen ? <CheckCheck size={13} /> : <Check size={13} />)}
          {editable && <button type="button" className="icon-btn" style={{ width: 18, height: 18, opacity: 0.85 }} aria-label="Modifica messaggio" onClick={() => { setDraft(message.body); setEditing(true); }}><Pencil size={11} /></button>}
        </div>
      </div>
    </div>
  );
}

export function ChatThread({ channel }: { channel: ChatChannel }) {
  const { orgId, me } = useOrgCtx();
  const messages = useChatMessages(orgId, channel.id);
  const send = useSendMessage(orgId, channel.id);
  const action = useChannelAction(orgId);
  const rename = useRenameConversation(orgId);
  const addMember = useAddChannelMember(orgId);
  const wsMembers = useWorkspace(orgId, channel.workspaceId ?? undefined);
  const toast = useToast();
  const [text, setText] = useState('');
  const [invite, setInvite] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(channel.name);
  const [menuOpen, setMenuOpen] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages.data?.length, channel.id]);

  const submit = () => {
    const body = text.trim();
    if (!body) return;
    setText('');
    send.mutate(body, { onError: (e) => toast(e instanceof ApiError ? e.message : 'Invio non riuscito') });
  };

  const icon = channel.kind === 'dm' ? <Avatar id={channel.peer?.id ?? channel.id} name={channel.name} size={30} /> :
    channel.kind === 'workspace' ? <span className="list-icon" style={{ background: 'var(--fill)', color: 'var(--label)' }}>{channel.isPrivate ? <Lock size={14} /> : <Hash size={14} />}</span> :
      <span className="list-icon" style={{ background: 'var(--ws-indigo)' }}>{channel.name.slice(0, 1).toUpperCase()}</span>;

  return (
    <>
      <div className="chat-head">
        {icon}
        <span className="grow" style={{ minWidth: 0 }}>
          <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
            {channel.kind === 'workspace' ? `#${channel.name}` : channel.name}
          </strong>
          {channel.kind === 'dm' && channel.peer?.nickname && <div className="secondary">@{channel.peer.nickname}</div>}
          {channel.kind === 'group' && <div className="secondary">{channel.memberCount} persone</div>}
          {channel.topic && <div className="secondary">{channel.topic}</div>}
        </span>
        {!channel.isMember && channel.kind === 'workspace' && !channel.isPrivate && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => action.mutate({ id: channel.id, action: 'join' }, { onError: (e) => toast(e instanceof ApiError ? e.message : 'Operazione non riuscita') })}>
            Unisciti
          </button>
        )}
        {channel.canManage && channel.kind !== 'dm' && (
          <div style={{ position: 'relative' }}>
            <button type="button" className="icon-btn muted" aria-label="Altre opzioni" onClick={() => setMenuOpen((v) => !v)}><MoreVertical size={18} /></button>
            {menuOpen && (
              <div className="card" style={{ position: 'absolute', top: 34, right: 0, zIndex: 5, minWidth: 170, boxShadow: 'var(--shadow-2)' }}>
                <div className="list">
                  {channel.kind === 'group' && <button type="button" className="list-row" onClick={() => { setMenuOpen(false); setInvite(true); }}><UserPlus size={16} /> Aggiungi persone</button>}
                  {channel.kind === 'workspace' && channel.isPrivate && <button type="button" className="list-row" onClick={() => { setMenuOpen(false); setInvite(true); }}><UserPlus size={16} /> Aggiungi persone</button>}
                  <button type="button" className="list-row" onClick={() => { setMenuOpen(false); setNewName(channel.kind === 'workspace' ? (channel.topic ?? '') : channel.name); setRenaming(true); }}>
                    {channel.kind === 'workspace' ? 'Modifica argomento' : 'Rinomina gruppo'}
                  </button>
                  <button
                    type="button" className="list-row" style={{ color: 'var(--red)' }}
                    onClick={() => {
                      setMenuOpen(false);
                      if (confirm(`Eliminare ${channel.kind === 'workspace' ? 'questo canale' : 'questo gruppo'}? La conversazione sparirà per tutti.`)) {
                        action.mutate({ id: channel.id, action: 'archive' }, { onError: (e) => toast(e instanceof ApiError ? e.message : 'Eliminazione non riuscita') });
                      }
                    }}
                  >
                    <Trash2 size={16} /> Elimina {channel.kind === 'workspace' ? 'canale' : 'gruppo'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="chat-messages">
        {messages.isLoading && <PageLoader />}
        {messages.data?.length === 0 && <EmptyState tone="neutral" title="Ancora nessun messaggio">Scrivi il primo messaggio.</EmptyState>}
        {messages.data?.map((m) => <ChatMessageRow key={m.id} message={m} channel={channel} orgId={orgId} />)}
        <div ref={endRef} />
      </div>
      {channel.canPost && (
        <div className="chat-composer">
          <input className="input" placeholder={channel.kind === 'workspace' ? `Scrivi in #${channel.name}` : `Scrivi a ${channel.name}`} value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }} />
          <button type="button" className="icon-btn" aria-label="Invia" onClick={submit} disabled={!text.trim() || send.isPending}><Send size={18} /></button>
        </div>
      )}

      <Sheet open={invite} onClose={() => setInvite(false)} variant="center" title="Aggiungi persone" labelledBy="invite-title">
        {channel.kind === 'group' ? (
          <p className="hint">Per aggiungere persone a un gruppo, creane uno nuovo con tutti i partecipanti: la modifica dei membri di un gruppo esistente arriverà presto.</p>
        ) : (
          <div className="list">
            {(wsMembers.data?.members ?? []).map((m) => (
              <button key={m.userId} type="button" className="list-row" disabled={m.userId === me.user.id}
                onClick={() => addMember.mutate({ channelId: channel.id, userId: m.userId }, { onSuccess: () => toast(`${m.displayName} aggiunto`) })}>
                <Avatar id={m.userId} name={m.displayName} size={30} />
                <span className="grow">{m.displayName}</span>
              </button>
            ))}
          </div>
        )}
      </Sheet>

      <Sheet open={renaming} onClose={() => setRenaming(false)} variant="center" title={channel.kind === 'workspace' ? 'Modifica argomento' : 'Rinomina gruppo'} labelledBy="rename-title"
        footer={
          <div style={{ display: 'flex', gap: 8, width: '100%' }}>
            <span className="spacer" />
            <button type="button" className="btn btn-secondary" onClick={() => setRenaming(false)}>Annulla</button>
            <button type="button" className="btn btn-primary" disabled={!newName.trim() || rename.isPending}
              onClick={() => rename.mutate(
                channel.kind === 'workspace' ? { id: channel.id, topic: newName } : { id: channel.id, name: newName },
                { onSuccess: () => setRenaming(false), onError: (e) => toast(e instanceof ApiError ? e.message : 'Operazione non riuscita') },
              )}>
              Salva
            </button>
          </div>
        }>
        <label className="field"><span>{channel.kind === 'workspace' ? 'Argomento' : 'Nome'}</span><input className="input" value={newName} onChange={(e) => setNewName(e.target.value)} /></label>
      </Sheet>
    </>
  );
}
